import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTheme } from '../../contexts/ThemeContext';
import NavBar from '../../components/NavBar';
import Footer from '../../components/Footer';
import { API_BASE_URL } from '../../config/api';
import './ForgotPassword.css';

const ForgotPassword: React.FC = () => {
    const navigate = useNavigate();
    const { isDarkMode } = useTheme();
    const [email, setEmail] = useState('');
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setError('');
        setIsLoading(true);

        try {
            const response = await fetch(`${API_BASE_URL}/api/v1/auth/forgot-password`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ email }),
            });

            // Handle non-JSON responses (like 404 from server)
            if (response.status === 404 && !response.headers.get('content-type')?.includes('application/json')) {
                setError('Email is not linked to an Account');
                setIsLoading(false);
                return;
            }

            let data;
            try {
                data = await response.json();
            } catch (jsonError) {
                // If response is not JSON, check status code
                if (response.status === 404) {
                    setError('Email is not linked to an Account');
                } else {
                    setError('An error occurred. Please try again.');
                }
                setIsLoading(false);
                return;
            }

            if (response.ok) {
                // Auto-redirect to reset password page with email as URL parameter
                navigate(`/reset-password?email=${encodeURIComponent(email)}`);
            } else {
                // Handle specific error cases
                if (response.status === 404 && data.detail === 'email_not_found') {
                    setError('Email is not linked to an Account');
                } else if (response.status === 400 && data.detail === 'google_account_only') {
                    setError('This account must be signed up using Google Sign in');
                } else if (response.status === 404) {
                    setError('Email is not linked to an Account');
                } else {
                    setError(data.detail || 'Failed to send password reset code');
                }
            }
        } catch (error) {
            // Handle network errors
            setError('An error occurred. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className={`forgot-password-page ${isDarkMode ? 'dark-mode' : ''}`}>
            <NavBar />
            <div className="forgot-password-container">
                <div className="forgot-password-form-container">
                    <div className="forgot-password-header">
                        <h1>Forgot Password</h1>
                        <p>Enter your email address and we'll send you a code to reset your password.</p>
                    </div>

                    <div className="forgot-password-content">
                        <form onSubmit={handleSubmit} className="forgot-password-form">
                            {error && (
                                <div className="forgot-password-error-message">
                                    {error}
                                </div>
                            )}
                            <div className="form-group">
                                <input
                                    type="email"
                                    name="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    placeholder="Email"
                                    disabled={isLoading}
                                />
                            </div>

                            <button
                                type="submit"
                                className="forgot-password-submit-btn"
                                disabled={isLoading}
                            >
                                {isLoading ? 'Sending...' : 'Send Reset Code'}
                            </button>
                        </form>

                        <div className="forgot-password-footer">
                            <p>
                                Remember your password?{' '}
                                <Link to="/" className="signin-link">
                                    Sign in
                                </Link>
                            </p>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default ForgotPassword;

